import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Heading } from "react-aria-components";
import z from "zod";

import { Alert } from "@/components/base/alert";
import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import type { Division, TournamentDivision } from "@/db/schema";
import { getTournamentDivisionDisplay } from "@/hooks/tournament";
import { useTRPC } from "@/integrations/trpc/react";
import { isDefined } from "@/utils/types";

import { useActiveTeams } from "../context";

const createPoolsSchema = z.object({
  id: z.number(),
  count: z.number(),
  overwrite: z.boolean(),
});

export type CreatePoolsFormProps = {
  tournamentId: number;
  division: TournamentDivision & { division: Division };
  teamCount?: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function idealPoolCount(teamCount: number | undefined): number {
  if (!teamCount || teamCount <= 5) return 1;
  return Math.round(teamCount / 5);
}

const NUMBER_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
];

function toWord(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}

export function poolDistributionText(
  teamCount: number,
  poolCount: number,
): string {
  if (poolCount <= 0 || teamCount <= 0) return "";
  const baseSize = Math.floor(teamCount / poolCount);
  const remainder = teamCount % poolCount;

  const parts: string[] = [];
  if (remainder > 0) {
    const largeSize = baseSize + 1;
    parts.push(
      `${remainder} ${toWord(largeSize)} team pool${remainder > 1 ? "s" : ""}`,
    );
  }
  const smallCount = poolCount - remainder;
  if (smallCount > 0 && baseSize > 0) {
    parts.push(
      `${smallCount} ${toWord(baseSize)} team pool${smallCount > 1 ? "s" : ""}`,
    );
  }

  return parts.join(" and ");
}

export function CreatePoolsForm({
  tournamentId,
  division,
  teamCount,
  onOpenChange,
  ...props
}: CreatePoolsFormProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutateAsync, failureReason } = useMutation({
    ...trpc.tournaments.createPools.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());

      onOpenChange(false);
    },
  });

  const schema = createPoolsSchema.pick({
    count: true,
    overwrite: true,
  });

  const teams = useActiveTeams(division.id);
  const resolvedTeamCount = teamCount ?? teams?.length;

  const form = useAppForm({
    defaultValues: {
      count: idealPoolCount(resolvedTeamCount),
      overwrite: false,
    },
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: async ({ value: { count, overwrite } }) => {
      await mutateAsync({
        id: division.id,
        count,
        overwrite,
      });
    },
  });

  const displayTeamCount = resolvedTeamCount ?? "loading...";
  const allTeamsSeeded = teams?.every(({ seed }) => isDefined(seed)) ?? false;

  return (
    <Modal {...props} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Create Pools
        </Heading>

        <p className="mb-6 text-sm text-gray-700">
          Create pools for the{" "}
          <span className="font-bold italic">
            {getTournamentDivisionDisplay(division)}
          </span>{" "}
          division. If you want to recreate pools entirely, select{" "}
          <span className="font-semibold italic">Overwrite existing</span>.
        </p>

        {displayTeamCount && (
          <p className="mb-6 text-sm text-gray-700">
            There are{" "}
            <span className="font-bold italic">{displayTeamCount}</span> teams
            in this division.
          </p>
        )}

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {!allTeamsSeeded && (
            <Alert
              color="warning"
              title="Missing seeds"
              description='All teams must have seeds before pools can be created. Use "Calculate Seeds" first or manually set it for any team in the teams tab.'
            />
          )}

          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to create pools"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppField
            name="count"
            children={(field) => (
              <field.Number label="Pool Count" field={field} minValue={1} />
            )}
          />

          {typeof displayTeamCount === "number" && (
            <form.Subscribe selector={(state) => state.values.count}>
              {(count) => {
                const text = poolDistributionText(displayTeamCount, count);
                return text ? (
                  <p className="-mt-4 text-sm text-gray-600">{text}</p>
                ) : null;
              }}
            </form.Subscribe>
          )}

          <form.AppField
            name="overwrite"
            children={(field) => (
              <field.Checkbox label="Overwrite existing" field={field} />
            )}
          />

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton
                requireChange={false}
                isDisabled={!allTeamsSeeded}
              >
                Create
              </form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
